import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Calendar } from './ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import {
  CalendarIcon,
  Loader2,
  PlusCircle,
  Pencil,
  Building2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  useAddVendorHourMutation,
  useUpdateVendorHourMutation,
  type VendorHour,
  type CreateVendorHourPayload,
} from '@/redux/service/vendor'

const VendorHoursSchema = Yup.object({
  date: Yup.string().required('Date is required'),
  hours: Yup.number()
    .typeError('Must be a number')
    .required('Hours are required')
    .min(0.5, 'Minimum 0.5 hours'),
  description: Yup.string(),
  rate: Yup.number().typeError('Must be a number').min(0, 'Must be positive'),
})

interface Props {
  open: boolean
  onClose: () => void
  projectId: number
  vendorId: number
  vendorName: string
  vendorDefaultRate?: number
  entry?: VendorHour | null
}

interface FormValues {
  date: string
  hours: number | string
  description: string
  rate: number | string
}

const CreateUpdateVendorHours = ({
  open,
  onClose,
  projectId,
  vendorId,
  vendorName,
  vendorDefaultRate,
  entry,
}: Props) => {
  const [dateOpen, setDateOpen] = useState(false)
  const [addVendorHour, { isLoading: isAdding }] = useAddVendorHourMutation()
  const [updateVendorHour, { isLoading: isUpdating }] =
    useUpdateVendorHourMutation()
  const isEdit = !!entry?.id
  const isLoading = isAdding || isUpdating

  const initialValues: FormValues = {
    date: entry?.date?.split('T')[0] ?? '',
    hours: entry?.hours ?? '',
    description: entry?.description ?? '',
    rate: entry?.rate ?? vendorDefaultRate ?? '',
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='max-h-[92vh] overflow-y-auto p-0 gap-0'>
        <DialogHeader className='px-6 pt-6 pb-4 border-b border-border'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/20 shrink-0'>
              {isEdit ? (
                <Pencil className='w-4 h-4 text-orange-500' />
              ) : (
                <PlusCircle className='w-4 h-4 text-orange-500' />
              )}
            </div>
            <div>
              <DialogTitle className='text-base font-semibold leading-tight'>
                {isEdit ? 'Update Vendor Hours' : 'Add Vendor Hours'}
              </DialogTitle>
              <DialogDescription className='text-xs text-muted-foreground mt-0.5'>
                {isEdit
                  ? 'Edit the fields below and save your changes.'
                  : 'Log hours worked by the vendor on this project.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Formik<FormValues>
          initialValues={initialValues}
          enableReinitialize
          validationSchema={VendorHoursSchema}
          onSubmit={async (values, { resetForm }) => {
            try {
              const payload: CreateVendorHourPayload = {
                vendor_id: vendorId,
                date: values.date,
                hours: Number(values.hours),
                ...(values.description
                  ? { description: values.description }
                  : {}),
                ...(values.rate !== '' && Number(values.rate) > 0
                  ? { rate: Number(values.rate) }
                  : {}),
              }

              if (isEdit && entry?.id) {
                await updateVendorHour({
                  projectId,
                  data: { id: entry.id, ...payload },
                }).unwrap()
                toast.success('Vendor hours updated')
              } else {
                await addVendorHour({ projectId, data: payload }).unwrap()
                toast.success('Vendor hours logged')
                resetForm()
              }
              onClose()
            } catch (err: unknown) {
              const error = err as { data?: { message?: string } }
              toast.error(error?.data?.message ?? 'Failed to save')
            }
          }}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            errors,
            touched,
            setFieldValue,
          }) => (
            <div className='px-6 py-5 space-y-5'>
              {/* Vendor (read-only badge) */}
              <div className='flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-orange-500/5 border border-orange-500/20'>
                <Building2 className='w-4 h-4 text-orange-500 shrink-0' />
                <div>
                  <p className='text-[10px] font-semibold uppercase tracking-wide text-orange-500/70'>
                    Vendor
                  </p>
                  <p className='text-sm font-medium text-foreground'>
                    {vendorName}
                  </p>
                </div>
              </div>

              {/* Date */}
              <div className='space-y-1.5'>
                <Label className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  Date <span className='text-destructive'>*</span>
                </Label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type='button'
                      variant='outline'
                      className={cn(
                        'h-9 w-full justify-start text-left font-normal text-sm border-input hover:bg-accent/30 transition-colors',
                        !values.date && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className='mr-2 h-3.5 w-3.5 opacity-50 shrink-0' />
                      {values.date
                        ? format(new Date(values.date), 'PP')
                        : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align='start'
                    className='w-auto p-0 border border-border shadow-lg rounded-xl'
                  >
                    <Calendar
                      mode='single'
                      captionLayout='dropdown'
                      fromYear={2018}
                      toYear={2035}
                      selected={values.date ? new Date(values.date) : undefined}
                      onSelect={date => {
                        setFieldValue(
                          'date',
                          date ? format(date, 'yyyy-MM-dd') : ''
                        )
                        setDateOpen(false)
                      }}
                    />
                  </PopoverContent>
                </Popover>
                {errors.date && touched.date && (
                  <p className='text-xs text-destructive'>{errors.date}</p>
                )}
              </div>

              {/* Hours */}
              <div className='space-y-1.5'>
                <Label
                  htmlFor='vh-hours'
                  className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'
                >
                  Hours <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='vh-hours'
                  name='hours'
                  type='number'
                  min={0}
                  step='0.5'
                  placeholder='e.g. 8'
                  value={values.hours}
                  onChange={handleChange}
                  className={cn(
                    'h-9 text-sm',
                    errors.hours && touched.hours
                      ? 'border-destructive focus-visible:ring-destructive/30'
                      : ''
                  )}
                />
                {errors.hours && touched.hours && (
                  <p className='text-xs text-destructive'>
                    {String(errors.hours)}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className='space-y-1.5'>
                <Label
                  htmlFor='vh-description'
                  className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'
                >
                  Description / Notes
                </Label>
                <Input
                  id='vh-description'
                  name='description'
                  placeholder='Optional notes about the work done…'
                  value={values.description}
                  onChange={handleChange}
                  className='h-9 text-sm'
                />
              </div>

              {/* Rate */}
              <div className='space-y-1.5'>
                <Label
                  htmlFor='vh-rate'
                  className='text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5'
                >
                  Rate ($/hr)
                  {vendorDefaultRate !== undefined && (
                    <span className='text-[10px] font-medium normal-case tracking-normal px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border'>
                      Default: ${vendorDefaultRate}/hr
                    </span>
                  )}
                </Label>
                <Input
                  id='vh-rate'
                  name='rate'
                  type='number'
                  min={0}
                  step='0.01'
                  placeholder={
                    vendorDefaultRate !== undefined
                      ? String(vendorDefaultRate)
                      : '0.00'
                  }
                  value={values.rate}
                  onChange={handleChange}
                  className={cn(
                    'h-9 text-sm',
                    errors.rate && touched.rate
                      ? 'border-destructive focus-visible:ring-destructive/30'
                      : ''
                  )}
                />
                {errors.rate && touched.rate && (
                  <p className='text-xs text-destructive'>
                    {String(errors.rate)}
                  </p>
                )}
              </div>

              {/* Auto-computed total cost */}
              {Number(values.hours) > 0 && Number(values.rate) > 0 && (
                <div className='flex items-center justify-between px-3 py-2.5 rounded-lg bg-muted/40 border border-border'>
                  <span className='text-xs font-medium text-muted-foreground'>
                    Estimated Total Cost
                  </span>
                  <span className='text-sm font-bold text-foreground'>
                    ${(Number(values.hours) * Number(values.rate)).toFixed(2)}
                  </span>
                </div>
              )}

              {/* Footer actions */}
              <div className='flex justify-end gap-2 pt-2 border-t border-border'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={onClose}
                  className='h-8 text-xs border-border hover:bg-accent/30'
                >
                  Cancel
                </Button>
                <Button
                  size='sm'
                  onClick={() => handleSubmit()}
                  disabled={isLoading}
                  className='h-8 text-xs min-w-[80px] bg-orange-500 hover:bg-orange-600 text-white border-orange-500'
                >
                  {isLoading ? (
                    <>
                      <Loader2 className='w-3.5 h-3.5 mr-1.5 animate-spin' />
                      Saving…
                    </>
                  ) : isEdit ? (
                    'Update'
                  ) : (
                    'Log Hours'
                  )}
                </Button>
              </div>
            </div>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default CreateUpdateVendorHours
